import { useState, useEffect, axios, Button, Input, Card, CardContent, CardHeader, CardTitle, Badge, Label, Loader2, Video, Sparkles, API, BACKEND_URL, useAuth, toast } from "@/pages/shared";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POLL_INTERVAL = 8000; // 8s

export default function VideosPage() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("vertical");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2");
  const [jobs, setJobs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [quota, setQuota] = useState(null);

  const fetchJobs = async () => {
    try { const r = await axios.get(`${API}/videos/jobs`); setJobs(r.data); } catch (e) {}
  };
  const fetchQuota = async () => {
    try { const r = await axios.get(`${API}/videos/quota`); setQuota(r.data); } catch (e) {}
  };

  useEffect(() => { fetchJobs(); fetchQuota(); }, []);

  // Poll active jobs
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (!hasActive) return;
    const t = setInterval(() => fetchJobs(), POLL_INTERVAL);
    return () => clearInterval(t);
  }, [jobs]);

  const create = async () => {
    if (prompt.trim().length < 10) { toast.error("Prompt must be at least 10 characters"); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/videos/generate`, { prompt: prompt.trim(), size, duration, model });
      toast.success(`Video queued! ETA ~${Math.round(r.data.eta_seconds / 60)} min`);
      setPrompt("");
      fetchJobs(); fetchQuota();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to queue video");
    }
    setCreating(false);
  };

  // OPTIMISTIC: allow interaction until quota API tells us otherwise.
  // Prevents entire form being stuck "no-entry" if the quota fetch fails or is slow.
  const canGenerate = !quota || (quota.limit > 0 && quota.remaining > 0);
  const upgradeRequired = quota && quota.limit === 0;

  return (
    <div className="space-y-6" data-testid="videos-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2"><Video className="w-8 h-8 text-violet-500" /> AI Video Studio</h1>
        <p className="text-muted-foreground">Generate short-form vertical videos for TikTok, Reels, and Shorts with Sora 2.</p>
      </div>

      {upgradeRequired && (
        <Card className="glass border-amber-500/30">
          <CardContent className="p-6">
            <Badge className="mb-3 bg-amber-600">Pro or Enterprise required</Badge>
            <p className="text-sm text-muted-foreground">Video generation is available on the Pro (£29/mo, 5 videos) and Enterprise (£99/mo, 50 videos) plans. Upgrade in Settings to unlock.</p>
          </CardContent>
        </Card>
      )}

      {quota && quota.limit > 0 && (
        <Card className="glass">
          <CardContent className="p-4 flex items-baseline justify-between">
            <p className="text-sm text-muted-foreground">This month</p>
            <p className="text-lg font-semibold">{quota.used} <span className="text-muted-foreground text-sm font-normal">/ {quota.limit} videos</span></p>
          </CardContent>
        </Card>
      )}

      <Card className="glass">
        <CardHeader><CardTitle>Create a new video</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Prompt (describe the scene)</Label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="A cinematic shot of a young entrepreneur typing on a laptop at sunrise, golden hour lighting, shallow depth of field..." className="mt-1 bg-white/5 border-white/10 h-24" disabled={!canGenerate} data-testid="videos-prompt-input" />
            <p className="text-xs text-muted-foreground mt-1">Tip: the more specific the shot — subject, lighting, camera angle — the better the result.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Aspect ratio</Label>
              <Select value={size} onValueChange={setSize} disabled={!canGenerate}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="videos-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">Vertical 9:16 (TikTok, Reels, Shorts)</SelectItem>
                  <SelectItem value="landscape">Landscape 16:9 (YouTube)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v))} disabled={!canGenerate}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="videos-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 seconds (~2 min to generate)</SelectItem>
                  <SelectItem value="8">8 seconds (~4 min)</SelectItem>
                  <SelectItem value="12">12 seconds (~6 min)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quality</Label>
              <Select value={model} onValueChange={setModel} disabled={!canGenerate}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="videos-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sora-2">Standard (Sora 2)</SelectItem>
                  <SelectItem value="sora-2-pro">Pro (higher fidelity, slower)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={create} disabled={!canGenerate || creating} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="videos-generate-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate video
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Your videos</h2>
        {jobs.length === 0 ? (
          <p className="text-muted-foreground text-sm">No videos yet. Create your first one above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((j) => (
              <Card key={j.id} className="glass" data-testid={`video-job-${j.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm line-clamp-2 flex-1">{j.prompt}</p>
                    <Badge variant={j.status === "completed" ? "default" : j.status === "failed" ? "destructive" : "secondary"}>{j.status}</Badge>
                  </div>
                  {j.status === "completed" && j.video_url ? (
                    <video controls className="w-full rounded-lg bg-black" data-testid={`video-player-${j.id}`}>
                      <source src={`${BACKEND_URL}${j.video_url}`} type="video/mp4" />
                    </video>
                  ) : j.status === "failed" ? (
                    <p className="text-xs text-destructive">{j.error || "Generation failed"}</p>
                  ) : (
                    <div className="h-32 rounded-lg bg-white/5 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                      <span className="ml-2 text-sm text-muted-foreground">{j.status === "queued" ? "Queued…" : "Rendering…"}</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{j.size} · {j.duration}s · {j.model}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
